import React from "react";
import { PrimaryButton, SecondaryButton, DangerButton } from "../../design-system/components/buttons";
import { typography, surfaces, borders, textColors } from "../../design-system/tokens";
import {
  PAYMENT_HEALTH_STATES,
  derivePaymentHealth,
  formatBillingAddressShort,
  formatCardExpiry,
  formatCardSummary,
} from "../utils/paymentMethodHelpers";

/**
 * Read-only payment-method card rendered on the subscription settings page.
 *
 * The component is presentational — it never calls Stripe directly. Parents
 * fetch `/api/stripe/payment-method/` and pass the result here, plus the
 * action callbacks that open the appropriate modal (update card / change
 * address / open Stripe portal / remove card).
 *
 * Health state drives the banner accents and copy via the
 * `paymentMethodHelpers` state machine — keep new conditional branches
 * inside that helper, not here.
 */

const HEALTH_TO_ACCENT = {
  [PAYMENT_HEALTH_STATES.HEALTHY]: {
    icon: "shield",
    iconColor: "text-emerald-500 dark:text-emerald-400",
    chipBg: "bg-emerald-50 dark:bg-emerald-900/20",
    chipText: "text-emerald-700 dark:text-emerald-300",
    chipLabel: "Active",
    bannerText: null,
  },
  [PAYMENT_HEALTH_STATES.EXPIRING_SOON]: {
    icon: "clock",
    iconColor: "text-amber-500 dark:text-amber-400",
    chipBg: "bg-amber-50 dark:bg-amber-900/20",
    chipText: "text-amber-700 dark:text-amber-300",
    chipLabel: "Expiring soon",
    bannerText: "Your card expires soon. Update it to avoid a failed renewal.",
  },
  [PAYMENT_HEALTH_STATES.EXPIRED]: {
    icon: "alert",
    iconColor: "text-red-500 dark:text-red-400",
    chipBg: "bg-red-50 dark:bg-red-900/20",
    chipText: "text-red-700 dark:text-red-300",
    chipLabel: "Expired",
    bannerText: "Your saved card is expired. Add a new card to keep your subscription active.",
  },
  [PAYMENT_HEALTH_STATES.PAST_DUE]: {
    icon: "alert",
    iconColor: "text-red-500 dark:text-red-400",
    chipBg: "bg-red-50 dark:bg-red-900/20",
    chipText: "text-red-700 dark:text-red-300",
    chipLabel: "Payment failed",
    bannerText:
      "Your last payment failed. Update your card to retry the charge and reactivate your subscription.",
  },
  [PAYMENT_HEALTH_STATES.MISSING]: {
    icon: "alert",
    iconColor: "text-amber-500 dark:text-amber-400",
    chipBg: "bg-amber-50 dark:bg-amber-900/20",
    chipText: "text-amber-700 dark:text-amber-300",
    chipLabel: "No card on file",
    bannerText: "Add a payment method so we can renew your subscription.",
  },
  [PAYMENT_HEALTH_STATES.NO_PAID]: {
    icon: "shield",
    iconColor: "text-blue-500 dark:text-blue-400",
    chipBg: "bg-blue-50 dark:bg-blue-900/20",
    chipText: "text-blue-700 dark:text-blue-300",
    chipLabel: "Saved",
    bannerText: null,
  },
};

const ICONS = {
  shield: (cls = "") => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cls} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l8 4v5c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V7l8-4z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
    </svg>
  ),
  clock: (cls = "") => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cls} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 2" />
    </svg>
  ),
  alert: (cls = "") => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={cls} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  ),
};

const CardChip = ({ health }) => {
  const accent = HEALTH_TO_ACCENT[health] || HEALTH_TO_ACCENT[PAYMENT_HEALTH_STATES.HEALTHY];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full ${typography.caption} font-medium ${accent.chipBg} ${accent.chipText}`}
    >
      {ICONS[accent.icon] ? ICONS[accent.icon]("w-3.5 h-3.5") : null}
      {accent.chipLabel}
    </span>
  );
};

const Banner = ({ health, onUpdate }) => {
  const accent = HEALTH_TO_ACCENT[health];
  if (!accent || !accent.bannerText) return null;
  const isUrgent =
    health === PAYMENT_HEALTH_STATES.PAST_DUE ||
    health === PAYMENT_HEALTH_STATES.EXPIRED ||
    health === PAYMENT_HEALTH_STATES.MISSING;
  const bannerColor = isUrgent
    ? "border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20"
    : "border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20";
  const textColor = isUrgent
    ? "text-red-700 dark:text-red-300"
    : "text-amber-700 dark:text-amber-300";
  return (
    <div
      role="alert"
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border ${bannerColor} px-4 py-3`}
    >
      <p className={`${typography.bodySmall} ${textColor}`}>{accent.bannerText}</p>
      <PrimaryButton size="sm" onClick={onUpdate}>
        {health === PAYMENT_HEALTH_STATES.PAST_DUE ? "Update & retry" : "Update payment method"}
      </PrimaryButton>
    </div>
  );
};

const PaymentMethodSection = ({
  paymentMethodDetails,
  loading = false,
  error = null,
  onUpdatePaymentMethod,
  onRemovePaymentMethod,
  onUpdateBillingAddress,
  onOpenBillingPortal,
  removeLoading = false,
  removeError = null,
  portalLoading = false,
  portalError = null,
}) => {
  if (loading) {
    return (
      <section
        aria-label="Payment method"
        className={`rounded-2xl shadow-sm ${surfaces.glassCard} border ${borders.glass} overflow-hidden`}
      >
        <div className="px-4 sm:px-6 py-5">
          <div className={`${typography.bodySmall} ${textColors.secondary}`}>
            Loading payment method…
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        aria-label="Payment method"
        className={`rounded-2xl shadow-sm ${surfaces.glassCard} border ${borders.glass} overflow-hidden`}
      >
        <div className="px-4 sm:px-6 py-5">
          <div className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>
            {error}
          </div>
        </div>
      </section>
    );
  }

  const details = paymentMethodDetails || {};
  const defaultPm = details.default_payment_method || null;
  const billingAddress = details.billing_address || null;
  const subscriptionStatus = details.subscription_status || "";
  const hasActivePaidSubscription = !!details.has_active_paid_subscription;
  const savedPaymentMethods = Array.isArray(details.saved_payment_methods)
    ? details.saved_payment_methods
    : [];
  const otherSavedMethods = savedPaymentMethods.filter(
    (pm) => !pm.is_default && pm.stripe_payment_method_id
  );

  const health = derivePaymentHealth({
    paymentMethod: defaultPm,
    hasActivePaidSubscription,
    subscriptionStatus,
  });
  const accent = HEALTH_TO_ACCENT[health] || HEALTH_TO_ACCENT[PAYMENT_HEALTH_STATES.HEALTHY];

  const cardSummary = defaultPm ? formatCardSummary(defaultPm) : null;
  const cardExpiry = defaultPm ? formatCardExpiry(defaultPm) : null;
  const billingAddressLabel = formatBillingAddressShort(billingAddress);

  const canRemoveDefault = !hasActivePaidSubscription;

  return (
    <section
      aria-label="Payment method"
      className={`rounded-2xl shadow-sm ${surfaces.glassCard} border ${borders.glass} overflow-hidden`}
    >
      <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`${typography.h5} ${textColors.primary}`}>Payment method</h3>
              <CardChip health={health} />
            </div>
            <p className={`${typography.caption} ${textColors.tertiary}`}>
              Used for upcoming subscription invoices.
            </p>
          </div>
        </div>

        <Banner health={health} onUpdate={onUpdatePaymentMethod} />

        <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-4 space-y-3`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Default card</div>
              {defaultPm ? (
                <div className="space-y-0.5">
                  <div className={`${typography.h6} ${textColors.primary} truncate`}>
                    {cardSummary}
                  </div>
                  {cardExpiry ? (
                    <div className={`${typography.caption} ${textColors.tertiary}`}>
                      Expires {cardExpiry}
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                  No card on file yet.
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 self-start">
              <SecondaryButton
                size="sm"
                onClick={onUpdatePaymentMethod}
                aria-label={defaultPm ? "Update payment method" : "Add payment method"}
              >
                {defaultPm ? "Update" : "Add card"}
              </SecondaryButton>

              {defaultPm && onRemovePaymentMethod ? (
                <DangerButton
                  size="sm"
                  variant="outline"
                  onClick={onRemovePaymentMethod}
                  loading={removeLoading}
                  disabled={!canRemoveDefault || removeLoading}
                  aria-label="Remove payment method"
                  title={
                    canRemoveDefault
                      ? undefined
                      : "Add another card and set it as default before removing this one."
                  }
                >
                  Remove
                </DangerButton>
              ) : null}
            </div>
          </div>

          <div
            className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-3 border-t ${borders.glass}`}
          >
            <div className="min-w-0">
              <div className={`${typography.caption} ${textColors.tertiary}`}>Tax billing address</div>
              <div className={`${typography.bodySmall} ${textColors.primary} truncate`}>
                {billingAddressLabel || "Not set"}
              </div>
            </div>
            <button
              type="button"
              onClick={onUpdateBillingAddress}
              className={`${typography.bodySmall} text-blue-600 dark:text-blue-500 hover:text-blue-700 dark:hover:text-blue-400 underline self-start sm:self-auto`}
            >
              {billingAddress ? "Edit address" : "Set address"}
            </button>
          </div>

          {removeError ? (
            <p
              role="alert"
              className={`${typography.caption} text-red-600 dark:text-red-400`}
            >
              {removeError}
            </p>
          ) : null}
        </div>

        {otherSavedMethods.length > 0 ? (
          <div className={`rounded-xl border ${borders.default} ${surfaces.secondary} p-4 space-y-2`}>
            <div className={`${typography.bodySmall} ${textColors.secondary}`}>
              Other saved cards
            </div>
            <ul className="space-y-1">
              {otherSavedMethods.map((pm) => (
                <li
                  key={pm.stripe_payment_method_id}
                  className={`flex items-center justify-between gap-3 ${typography.bodySmall} ${textColors.primary}`}
                >
                  <span className="truncate">
                    {formatCardSummary(pm)} · expires {formatCardExpiry(pm) || "—"}
                  </span>
                </li>
              ))}
            </ul>
            <p className={`${typography.caption} ${textColors.tertiary}`}>
              To make one of these the default, click <strong>Update</strong> above and pick a card.
            </p>
          </div>
        ) : null}

        <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-1`}>
          <div className={`${typography.caption} ${textColors.tertiary}`}>
            Need a receipt or past invoice? View them in Stripe.
          </div>
          <SecondaryButton
            size="sm"
            onClick={onOpenBillingPortal}
            loading={portalLoading}
            aria-label="View invoices on Stripe"
          >
            View invoices
          </SecondaryButton>
        </div>

        {portalError ? (
          <p
            role="alert"
            className={`${typography.caption} text-red-600 dark:text-red-400`}
          >
            {portalError}
          </p>
        ) : null}
      </div>
    </section>
  );
};

export default PaymentMethodSection;
